import { useComputedColorScheme } from '@mantine/core';
import { useEffect, useRef } from 'react';
import { useState } from 'react';
import type { TestType } from '@prisma/client';
import clsx from 'clsx';
import ProjectLoader from './ProjectLoader';

export default function BrowserLoader({
  size,
  testType,
}: {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl';
  testType: TestType;
}) {
  const states = [
    testType === 'MOBILE_APP' ? 'Launching Emulator' : 'Launching VM',
    'Setting up environment',
    'Installing dependencies',
    'Connecting to VM',
    'Finishing up',
  ];

  const statusUpdateInterval = 4000;
  const fadeInDuration = 500;
  const fadeOutDuration = 500;
  const visibleDuration = 1000;

  const currentState = useRef(states[0]);
  const [displayState, setDisplayState] = useState(states[0]);
  const [isVisible, setIsVisible] = useState(true);

  const colorScheme = useComputedColorScheme();
  const isDark = colorScheme === 'dark';

  // Independently update the current state
  useEffect(() => {
    const interval = setInterval(() => {
      const currentIndex = states.indexOf(currentState.current);
      const nextIndex = (currentIndex + 1) % states.length;
      currentState.current = states[nextIndex];
    }, statusUpdateInterval);

    return () => clearInterval(interval);
  }, []);

  // Independently handle the animation cycle
  useEffect(() => {
    const runAnimationCycle = () => {
      // Start with fade in
      setIsVisible(true);

      // Schedule fade out
      const fadeOutTimer = setTimeout(() => {
        setIsVisible(false);

        // Schedule text update after fade out completes
        const updateTextTimer = setTimeout(() => {
          setDisplayState(currentState.current);

          // Start the next cycle
          runAnimationCycle();
        }, fadeOutDuration);

        return () => clearTimeout(updateTextTimer);
      }, fadeInDuration + visibleDuration);

      return () => clearTimeout(fadeOutTimer);
    };

    // Start the animation cycle
    const animationTimer = setTimeout(runAnimationCycle, 0);

    return () => clearTimeout(animationTimer);
  }, []);

  return (
    <div className="flex h-full flex-col items-center justify-center gap-2">
      <ProjectLoader
        invisibleColor={isDark ? '#2C2C2C' : '#BFBFBF'}
        delay={1.5}
      />
      <div
        className={clsx(
          'mt-1 min-w-[200px] text-center font-medium text-white transition-opacity ease-in-out',
          size ? `text-${size}` : 'text-lg',
          isVisible ? 'opacity-100' : 'opacity-0',
        )}
        style={{
          transitionDuration: `${isVisible ? fadeInDuration : fadeOutDuration}ms`,
        }}
      >
        {displayState}
      </div>
    </div>
  );
}
